import { type MouseEvent } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import classNames from 'classnames';

import { useFragmentContext } from '@jsmdg/react-fragment-scripts/fragment';
import { CountryCode, Tenant } from '@jsmdg/tenancy';
import { Button, ButtonColor, ButtonSize, CheckCircleIcon, RenderType } from '@jsmdg/yoshi';
import { type FragmentContext } from '../../../shared/types/fragmentContext';
import { type Product } from '../../../shared/types/searchResponse';
import { getProductTileMessages } from '../../helper/getProductTileMessages';
import styles from './BestsellerValueVoucherSlider.module.scss';

type VoucherProductDetailsProps = {
    readonly product: Product;
    readonly onSelectAmountClick: (
        event?: MouseEvent<HTMLAnchorElement | HTMLButtonElement>,
    ) => Promise<void>;
};

const VALUE_VOUCHER_MIN_PRICE = 20;

const productTileMessages = getProductTileMessages();

const messages = defineMessages({
    selectAmount: { defaultMessage: 'Betrag wählen' },
    benefitGiftWrap: {
        defaultMessage: 'Mit Geschenkverpackung oder direkt als PDF',
    },
    benefitValidity: {
        defaultMessage: '{years} Jahre gültig ab Ende des Kaufjahres',
    },
    benefitExperiences: {
        defaultMessage: 'Einlösbar in über 9.000 Erlebnisse',
    },
});

const VoucherProductDetails = ({
    onSelectAmountClick,
    product,
}: VoucherProductDetailsProps): JSX.Element => {
    const intl = useIntl();
    const { tenant, tenantConfig } = useFragmentContext<FragmentContext>();
    const formattedPrice = intl.formatNumber(VALUE_VOUCHER_MIN_PRICE, {
        style: 'currency',
        currency: tenantConfig.currency.code,
    });
    const pricePrefix = intl.formatMessage(productTileMessages.pricePrefix);
    const benefits = [
        intl.formatMessage(messages.benefitGiftWrap),
        intl.formatMessage(messages.benefitValidity, {
            years: new Tenant(tenant).belongsTo(CountryCode.Germany) ? 3 : 10,
        }),
        intl.formatMessage(messages.benefitExperiences),
    ];
    const selectAmountLabel = intl.formatMessage(messages.selectAmount);

    return (
        <div className="d-flex justify-content-between flex-column flex-sm-row mt-1x px-2x pl-sm-0 gap-2x">
            <div className="d-flex flex-column gap-2x">
                <div>
                    <p className={classNames('theme-text-headline mb-1x p-0', styles.productTitle)}>
                        {product.title}
                    </p>
                    <p className={classNames('m-0 d-sm-none', styles.productPrice)}>
                        {pricePrefix} {formattedPrice}
                    </p>
                </div>

                <ul className="d-flex flex-column m-0 p-0">
                    {benefits.map(benefit => (
                        <li
                            key={benefit}
                            className={classNames(
                                'd-flex align-items-start gap-0-5x',
                                styles.benefitItem,
                            )}
                        >
                            <CheckCircleIcon
                                className={classNames('flex-shrink-0', styles.benefitIcon)}
                            />
                            <span>{benefit}</span>
                        </li>
                    ))}
                </ul>
            </div>

            <div className="d-flex flex-column align-items-sm-end justify-content-between">
                <p className={classNames('m-0 d-none d-sm-block', styles.productPrice)}>
                    {pricePrefix} {formattedPrice}
                </p>
                <Button
                    a11yText={selectAmountLabel}
                    as={RenderType.Link}
                    className={classNames('mt-1x mt-sm-0', styles.button)}
                    color={ButtonColor.Brand}
                    size={ButtonSize.Small}
                    href={product.url}
                    internal
                    onClick={onSelectAmountClick}
                >
                    {selectAmountLabel}
                </Button>
            </div>
        </div>
    );
};

export { VoucherProductDetails };
